import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "@mui/material";
import { ArrowBackRounded, PersonOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import AuthLayout, { authStyles as styles } from "../../components/customComponents/authLayout/AuthLayout";
import { authApi } from "../../auth/authApi";
import { setAuth } from "../../store/actions";
import { brandConfig, loginConfig } from "../../auth/config";
import { authErrorMessage, authStateFromGarmentLogin } from "../../utils/authSession";

const ACCOUNT_TYPES = [
  { id: "manufacturer", label: "Manufacturer" },
  { id: "brand", label: "Brand" },
];

/**
 * Username + password sign-in against the Garment backend. Brands and manufacturers have separate
 * endpoints (POST /api/auth/login and /api/auth/manufacturer/login), so the user picks which
 * account they are signing in to.
 */
export default function LoginPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth?.accessToken);

  const [accountType, setAccountType] = useState("manufacturer");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (accessToken) {
    return <Navigate to="/splash" replace />;
  }

  const canSubmit = !!userName.trim() && !!password && !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setError("");
    setSubmitting(true);
    try {
      const login = accountType === "brand" ? authApi.brandLogin : authApi.manufacturerLogin;
      const res = await login({ userName: userName.trim(), password });
      const authState = authStateFromGarmentLogin(res?.data, accountType);
      if (!authState.accessToken) throw new Error("Login response carried no jwtToken");
      dispatch(setAuth(authState));
      navigate("/splash", { replace: true }); // splash routes to the role's dashboard
    } catch (err) {
      console.error("Password login failed:", err);
      setError(authErrorMessage(err, "Sign-in failed. Please try again."));
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      heading="Sign in"
      subheading={`Enter the email or phone number and password for your ${brandConfig.workspaceName} account.`}
      footer={
        loginConfig.methods.length > 1 ? (
          <div className={styles.footerRow}>
            <button type="button" className={styles.linkButton} onClick={() => navigate("/")}>
              <ArrowBackRounded fontSize="inherit" /> Other ways to sign in
            </button>
          </div>
        ) : undefined
      }
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.segmented} role="radiogroup" aria-label="Account type">
          {ACCOUNT_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              role="radio"
              aria-checked={accountType === type.id}
              className={`${styles.segment} ${accountType === type.id ? styles.segmentActive : ""}`}
              onClick={() => {
                setError("");
                setAccountType(type.id);
              }}
              disabled={submitting}
            >
              {type.label}
            </button>
          ))}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.visuallyHidden} htmlFor="password-login-username">
            Email or phone number
          </label>
          <input
            id="password-login-username"
            type="text"
            placeholder="Email or phone number"
            className={styles.inputField}
            value={userName}
            onChange={(e) => {
              setError("");
              setUserName(e.target.value);
            }}
            autoComplete="username"
            disabled={submitting}
            autoFocus
          />
          <PersonOutlined className={styles.trailingIcon} />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.visuallyHidden} htmlFor="password-login-password">
            Password
          </label>
          <input
            id="password-login-password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            className={styles.inputField}
            value={password}
            onChange={(e) => {
              setError("");
              setPassword(e.target.value);
            }}
            autoComplete="current-password"
            disabled={submitting}
          />
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setShowPassword((prev) => !prev)}
            onMouseDown={(e) => e.preventDefault()}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </button>
        </div>

        {/* Remember me + Forgot password removed: the Garment backend has no refresh token or
            password-reset endpoint yet. The forgot-password page is still at
            /login/password/forgot (LMS contract) for when it does. */}

        {error && (
          <Alert severity="error" className={styles.alert}>
            {error}
          </Alert>
        )}

        <button type="submit" className={styles.primaryButton} disabled={!canSubmit}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}
